/**
 * Industry Detail Page
 * Comprehensive industry research data with 6 tabs
 */

import React, { useState } from 'react';
import { parseNumber, formatLargeNumber, formatLargeCurrency } from '../lib/utils';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  TrendingUp,
  ArrowLeft,
  RefreshCw,
  Edit,
  Trash2,
  CheckCircle2,
  AlertTriangle,
  Clock,
  BarChart3,
  BarChart2,
  Shield,
  Zap,
  Users,
  Globe,
  Calendar,
  Loader2,
  ExternalLink,
  Building2,
  Plus,
  Sparkles,
  Search,
  X,
  UserPlus,
  Info,
} from 'lucide-react';
import IndustryFinancialBenchmarks from '../components/industry/IndustryFinancialBenchmarks';
import CompanyKPIBadges from '../components/financial/sections/CompanyKPIBadges';
import { useAllSummary } from '../components/financial/hooks/useCompanyFinancials';
import Select from '../components/ui/Select';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import { searchCompaniesInDatabase } from '../lib/api/financialApi';

import {
  getIndustry,
  generateIndustryResearch,
  listIndustryVersions,
  getIndustryTimeline,
  enrichCompetitiveLandscape,
  addCompanyToResearch,
  addCompetitorToLandscape,
  deleteIndustry,
} from '../lib/api/industryResearchApi';
import { standaloneCompaniesAPI, indiaCompanyAPI, ukCompanyAPI } from '../lib/api';
import { useLiveCountriesStore } from '../stores/liveCountriesStore';
import { useAppStore } from '../stores/appStore';
import type {
  IndustryDetail as IndustryDetailType,
  IndustryVersion,
  IndustryTimeline,
  DataRefreshStatus,
} from '../types/industryResearch';
import {
  STATUS_COLORS,
  DATA_REFRESH_STATUS_COLORS,
  DATA_REFRESH_STATUS_LABELS,
  getDataAge,
  getFreshnessColor,
} from '../types/industryResearch';

type TabKey =
  | 'overview'
  | 'market'
  | 'competitive'
  | 'technology'
  | 'disruptions'
  | 'financial'
  | 'versions';

const IndustryDetail: React.FC = () => {
  const { industryId } = useParams<{ industryId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabKey>('overview');

  // Fetch industry data
  const {
    data: industry,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['industry', industryId],
    queryFn: () => getIndustry(industryId!),
    enabled: !!industryId,
  });

  // Fetch versions
  const { data: versions } = useQuery({
    queryKey: ['industry-versions', industryId],
    queryFn: () => listIndustryVersions(industryId!),
    enabled: !!industryId && activeTab === 'versions',
  });

  // Fetch timeline
  const { data: timeline } = useQuery({
    queryKey: ['industry-timeline', industryId],
    queryFn: () => getIndustryTimeline(industryId!),
    enabled: !!industryId && activeTab === 'versions',
  });

  // Refresh research mutation
  const refreshMutation = useMutation({
    mutationFn: () => generateIndustryResearch(industryId!, true, 'user_001'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['industry', industryId] });
      refetch();
    },
  });

  const currentUser = useAppStore((state) => state.currentUser);

  // Delete (soft-delete → archive) mutation
  const deleteMutation = useMutation({
    mutationFn: () =>
      deleteIndustry(industryId!, currentUser?.email || currentUser?.id || 'system'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['industries'] });
      toast.success('Industry deleted');
      navigate('/industries');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || err?.message || 'Failed to delete industry');
    },
  });

  // Themed-confirm flow for industry deletion. Replaces the browser's native
  // window.confirm() so the UX matches the rest of the app and the dialog
  // can show our usual icons, spinners, and color tokens.
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const handleDeleteIndustry = () => setDeleteConfirmOpen(true);

  if (!industryId) {
    return (
      <div className="p-8">
        <p className="text-red-600">Industry ID is required</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <div className="max-w-screen-2xl mx-auto px-8 py-8">
          {/* Skeleton Header card */}
          <div className="bg-white border border-neutral-200 rounded-xl mb-6 animate-pulse">
            <div className="px-8 py-6">
              <div className="h-4 bg-neutral-200 rounded w-24 mb-4" />
              <div className="flex items-center justify-between">
                <div>
                  <div className="h-7 bg-neutral-200 rounded w-64 mb-2" />
                  <div className="flex gap-3 mt-2">
                    <div className="h-5 bg-neutral-100 rounded-full w-20" />
                    <div className="h-5 bg-neutral-100 rounded-full w-24" />
                    <div className="h-5 bg-neutral-100 rounded-full w-16" />
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="h-9 bg-neutral-200 rounded-lg w-32" />
                  <div className="h-9 bg-neutral-200 rounded-lg w-24" />
                </div>
              </div>
            </div>
            {/* Skeleton Tabs */}
            <div className="px-8 border-t border-neutral-200 flex gap-6">
              {Array.from({ length: 7 }).map((_, i) => (
                <div key={i} className="h-4 bg-neutral-200 rounded w-20 my-4" />
              ))}
            </div>
          </div>
          {/* Skeleton Content */}
          <div className="animate-pulse">
            <div className="grid grid-cols-4 gap-4 mb-8">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="bg-white rounded-lg border border-neutral-200 p-5">
                  <div className="h-3 bg-neutral-100 rounded w-1/2 mb-3" />
                  <div className="h-6 bg-neutral-200 rounded w-3/4" />
                </div>
              ))}
            </div>
            <div className="bg-white rounded-lg border border-neutral-200 p-6">
              <div className="h-5 bg-neutral-200 rounded w-1/3 mb-4" />
              <div className="space-y-3">
                <div className="h-3 bg-neutral-100 rounded w-full" />
                <div className="h-3 bg-neutral-100 rounded w-5/6" />
                <div className="h-3 bg-neutral-100 rounded w-4/6" />
                <div className="h-3 bg-neutral-100 rounded w-full" />
                <div className="h-3 bg-neutral-100 rounded w-3/4" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !industry) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <AlertTriangle className="w-12 h-12 text-red-600 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-red-900 mb-2">
            Failed to load industry
          </h3>
          <p className="text-red-700 mb-4">
            {error instanceof Error ? error.message : 'Unknown error'}
          </p>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const dataAge = getDataAge(industry.lastDataRefreshAt);
  const freshnessColor = getFreshnessColor(dataAge);

  const tabs: Array<{ key: TabKey; label: string; icon: any }> = [
    { key: 'overview', label: 'Overview', icon: TrendingUp },
    { key: 'market', label: 'Market Analysis', icon: BarChart3 },
    { key: 'competitive', label: 'Competitive Landscape', icon: Users },
    { key: 'technology', label: 'Technology & Trends', icon: Zap },
    { key: 'disruptions', label: 'Disruptions & Risks', icon: AlertTriangle },
    { key: 'financial', label: 'Financial Benchmarks', icon: BarChart2 },
    { key: 'versions', label: 'Version History', icon: Calendar },
  ];

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Inline refresh banner */}
      {refreshMutation.isPending && (
        <div className="bg-primary-50 border-b border-primary-200 py-3">
          <div className="max-w-screen-2xl mx-auto px-8 flex items-center gap-3">
            <Loader2 className="w-4 h-4 animate-spin text-primary-600 shrink-0" />
            <p className="text-sm text-primary-700">
              Refreshing industry research — this may take a few minutes. You can continue browsing.
            </p>
          </div>
        </div>
      )}

      <div className="max-w-screen-2xl mx-auto px-8 py-8">
        {/* Header card */}
        <div className="bg-white border border-neutral-200 rounded-xl mb-6">
          <div className="px-8 py-6">
            {/* Back Button */}
            <button
              onClick={() => navigate('/industries')}
              className="flex items-center gap-2 text-neutral-600 hover:text-neutral-900 mb-4"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Industry Library
            </button>

            {/* Title Row */}
            <div className="flex items-start justify-between mb-6">
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-neutral-900 mb-2 flex items-center gap-3">
                  {industry.displayName}
                </h1>
                <p className="text-neutral-600">{industry.category}</p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => refreshMutation.mutate()}
                  disabled={refreshMutation.isPending}
                  className="flex items-center gap-2 px-4 py-2 border border-neutral-300 rounded-lg hover:bg-neutral-50 disabled:opacity-50"
                >
                  <RefreshCw
                    className={`w-5 h-5 ${
                      refreshMutation.isPending ? 'animate-spin' : ''
                    }`}
                  />
                  Refresh Research
                </button>
                <button className="flex items-center gap-2 px-4 py-2 border border-neutral-300 rounded-lg hover:bg-neutral-50">
                  <Edit className="w-5 h-5" />
                  Edit
                </button>
                <button
                  onClick={handleDeleteIndustry}
                  disabled={deleteMutation.isPending}
                  className="flex items-center gap-2 px-4 py-2 border border-red-200 text-red-600 rounded-lg hover:bg-red-50 hover:border-red-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Trash2 className="w-5 h-5" />
                  {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </div>

            {/* Status Row */}
            <div className="flex items-center gap-4 flex-wrap">
              {/* Status Badge */}
              <span
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium ${
                  STATUS_COLORS[industry.status]
                }`}
              >
                {industry.status}
              </span>

              {/* Data Freshness */}
              <div
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm ${
                  DATA_REFRESH_STATUS_COLORS[industry.dataRefreshStatus]
                }`}
              >
                {industry.dataRefreshStatus === 'current' && (
                  <CheckCircle2 className="w-4 h-4" />
                )}
                {industry.dataRefreshStatus === 'pending' && (
                  <Clock className="w-4 h-4" />
                )}
                {industry.dataRefreshStatus === 'stale' && (
                  <AlertTriangle className="w-4 h-4" />
                )}
                {DATA_REFRESH_STATUS_LABELS[industry.dataRefreshStatus]}
              </div>

              {/* Version */}
              {industry.currentVersion && (
                <div className="flex items-center gap-2 text-sm text-neutral-600">
                  <BarChart3 className="w-4 h-4" />
                  Version {industry.currentVersion}
                </div>
              )}

              {/* Last Updated */}
              {industry.lastDataRefreshAt && (
                <div className={`text-sm ${freshnessColor}`}>
                  Updated{' '}
                  {dataAge !== null
                    ? `${dataAge} ${dataAge === 1 ? 'day' : 'days'} ago`
                    : 'recently'}
                </div>
              )}
            </div>
          </div>

          {/* Tabs — comfortable underline tabs that wrap onto multiple rows when
              they exceed the width (shared style with the Company Detail tab bar)
              so every tab stays readable and nothing is clipped. */}
          <div className="px-8">
            <nav className="flex flex-wrap gap-x-1 gap-y-1 border-t border-neutral-200">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`flex flex-shrink-0 items-center gap-2 whitespace-nowrap px-4 py-3 border-b-2 font-medium transition-colors ${
                      isActive
                        ? 'border-primary-600 text-primary-700'
                        : 'border-transparent text-neutral-600 hover:text-neutral-900 hover:border-neutral-300'
                    }`}
                  >
                    <Icon className={`h-5 w-5 ${isActive ? 'text-primary-600' : 'text-neutral-400'}`} />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div>
        {activeTab === 'overview' && (
          <OverviewTab industry={industry} />
        )}
        {activeTab === 'market' && <MarketTab industry={industry} />}
        {activeTab === 'competitive' && (
          <CompetitiveTab industry={industry} />
        )}
        {activeTab === 'technology' && (
          <TechnologyTab industry={industry} />
        )}
        {activeTab === 'disruptions' && (
          <DisruptionsTab industry={industry} />
        )}
        {activeTab === 'financial' && industryId && (
          <IndustryFinancialBenchmarks industryId={industryId} />
        )}
        {activeTab === 'versions' && (
          <VersionsTab
            industry={industry}
            versions={versions}
            timeline={timeline}
          />
        )}
        </div>
      </div>

      {/* Themed delete-confirm dialog. Replaces window.confirm() so the
          UX matches the rest of the app and stays responsive (spinner on
          the Delete button while the mutation is in flight). */}
      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        title={`Delete "${industry?.displayName ?? 'industry'}"?`}
        message={[
          'The industry will be archived.',
          'Historical versions and case snapshots are preserved.',
        ]}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={deleteMutation.isPending}
        onCancel={() => setDeleteConfirmOpen(false)}
        onConfirm={() => {
          deleteMutation.mutate(undefined, {
            onSettled: () => setDeleteConfirmOpen(false),
          });
        }}
      />
    </div>
  );
};

// ============================================================================
// TAB COMPONENTS
// ============================================================================

const OverviewTab: React.FC<{ industry: IndustryDetailType }> = ({
  industry,
}) => {
  const summary = industry.executiveSummary;

  if (!summary) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
        <Info className="w-12 h-12 text-blue-500 mx-auto mb-3" />
        <h3 className="text-lg font-semibold text-blue-900 mb-2">
          Research Not Yet Generated
        </h3>
        <p className="text-blue-700">
          Click "Refresh Research" above to generate AI-powered industry analysis.
          The Competitive Landscape and Financial Benchmarks tabs are available now.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white border border-neutral-200 rounded-lg p-6 flex flex-col justify-between min-h-[100px]">
          <div className="text-sm font-medium text-neutral-500">Industry Health</div>
          <div className="text-2xl font-bold text-neutral-900 capitalize mt-3">
            {summary.industryHealth}
          </div>
        </div>
        <div className="bg-white border border-neutral-200 rounded-lg p-6 flex flex-col justify-between min-h-[100px]">
          <div className="text-sm font-medium text-neutral-500">Attractiveness</div>
          <div className="text-2xl font-bold text-primary-600 capitalize mt-3">
            {summary.industryAttractiveness.replace('_', ' ')}
          </div>
        </div>
        <div className="bg-white border border-neutral-200 rounded-lg p-6 flex flex-col justify-between min-h-[100px]">
          <div className="text-sm font-medium text-neutral-500">Opportunity Score</div>
          <div className="text-2xl font-bold text-green-600 mt-3">
            {(parseNumber(summary.opportunityScore) * 100).toFixed(0)}%
          </div>
        </div>
        <div className="bg-white border border-neutral-200 rounded-lg p-6 flex flex-col justify-between min-h-[100px]">
          <div className="text-sm font-medium text-neutral-500">Risk Score</div>
          <div className="text-2xl font-bold text-orange-600 mt-3">
            {(parseNumber(summary.riskScore) * 100).toFixed(0)}%
          </div>
        </div>
      </div>

      {/* Executive Summary */}
      <div className="bg-white border border-neutral-200 rounded-lg p-6">
        <h2 className="text-xl font-semibold text-neutral-900 mb-4">
          Executive Summary
        </h2>
        {summary.overallSummary && summary.overallSummary.trim() ? (
          <p className="text-neutral-700 leading-relaxed whitespace-pre-wrap">
            {summary.overallSummary}
          </p>
        ) : (
          <p className="text-neutral-500 italic">
            Executive summary not available. The AI model may not have generated this content.
          </p>
        )}
      </div>

      {/* Key Insights */}
      {summary.keyInsights && summary.keyInsights.length > 0 && (
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-neutral-900 mb-4">
            Key Insights
          </h2>
          <ul className="space-y-2">
            {summary.keyInsights.map((insight, index) => (
              <li key={index} className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-neutral-700">{insight}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Strategic Implications */}
      {summary.strategicImplications && summary.strategicImplications.length > 0 && (
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-neutral-900 mb-4">
            Strategic Implications
          </h2>
          <ul className="space-y-2">
            {summary.strategicImplications.map((implication, index) => (
              <li key={index} className="flex items-start gap-3">
                <Zap className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                <span className="text-neutral-700">{implication}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Critical Success Factors */}
      {summary.criticalSuccessFactors && summary.criticalSuccessFactors.length > 0 && (
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-neutral-900 mb-4">
            Critical Success Factors
          </h2>
          <ul className="space-y-2">
            {summary.criticalSuccessFactors.map((factor, index) => (
              <li key={index} className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span className="text-neutral-700">{factor}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const MarketTab: React.FC<{ industry: IndustryDetailType }> = ({
  industry,
}) => {
  const market = industry.marketData;

  if (!market) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
        <Info className="w-12 h-12 text-blue-500 mx-auto mb-3" />
        <p className="text-blue-700">Market data available after research generation</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Market Size & Growth */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          <div className="text-sm text-neutral-600 mb-1">Market Size</div>
          <div className="text-3xl font-bold text-neutral-900">
            {formatLargeCurrency(parseNumber(market.totalMarketSize))}
          </div>
          <div className="text-xs text-neutral-500 mt-1">{market.currency}</div>
        </div>
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          <div className="text-sm text-neutral-600 mb-1">Growth Rate (CAGR)</div>
          <div className="text-3xl font-bold text-green-600">
            {parseNumber(market.marketGrowthRate).toFixed(1)}%
          </div>
        </div>
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          <div className="text-sm text-neutral-600 mb-1">Gross Margin</div>
          <div className="text-3xl font-bold text-blue-600">
            {parseNumber(market.averageGrossMargin).toFixed(1)}%
          </div>
        </div>
      </div>

      {/* Regional Breakdown */}
      {market.marketByRegion && Object.keys(market.marketByRegion).length > 0 && (
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-neutral-900 mb-4">
            Market by Region
          </h2>
          <div className="space-y-3">
            {Object.entries(market.marketByRegion).map(([region, size]) => (
              <div key={region} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Globe className="w-5 h-5 text-neutral-400" />
                  <span className="font-medium text-neutral-900">{region}</span>
                </div>
                <span className="text-neutral-700 font-semibold">{formatLargeCurrency(parseNumber(size))}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bulliesh Factors */}
      {market.primaryDrivers && market.primaryDrivers.length > 0 && (
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-neutral-900 mb-4">
            Primary Bulliesh Factors
          </h2>
          <ul className="space-y-2">
            {market.primaryDrivers.map((driver, index) => (
              <li key={index} className="flex items-start gap-3">
                <TrendingUp className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-neutral-700">{driver}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Fastest Growing Segment */}
      {market.fastestGrowingSegment && (
        <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-neutral-900 mb-2">
            Fastest Growing Segment
          </h3>
          <p className="text-neutral-700 text-lg">
            {market.fastestGrowingSegment}
          </p>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Supported countries for the "Add Competitor" dialog. Add a new entry here
// when a new region becomes available — the dropdown, search pipeline and
// API payload all derive from this list.
// ---------------------------------------------------------------------------
interface CountryOption {
  value: string;                          // internal key used by the UI
  label: string;                          // shown in the dropdown
  isoCode: 'US' | 'IN' | string;          // sent to the backend
  externalProvider: 'sec' | 'moneycontrol' | null; // null = DB-only
  searchPlaceholder: string;
}

const COMPETITOR_COUNTRIES: CountryOption[] = [
  {
    value: 'us',
    label: '🇺🇸 United States (SEC EDGAR)',
    isoCode: 'US',
    externalProvider: 'sec',
    searchPlaceholder: 'Search by company name or ticker (min 4 characters)…',
  },
  {
    value: 'india',
    label: '🇮🇳 India (NSE / BSE)',
    isoCode: 'IN',
    externalProvider: 'moneycontrol',
    searchPlaceholder: 'Search by company name (min 4 characters)…',
  },
  {
    value: 'uk',
    label: '🇬🇧 United Kingdom (Companies House)',
    isoCode: 'GB',
    externalProvider: null,
    searchPlaceholder: 'Search UK companies by name (min 4 characters)…',
  },
];

const CompetitiveTab: React.FC<{ industry: IndustryDetailType }> = ({
  industry,
}) => {
  const queryClient = useQueryClient();
  const competitive = industry.competitiveLandscape;
  const [enrichedLeaders, setEnrichedLeaders] = useState(competitive?.marketLeaders || []);
  const [leaderTimeRanges, setLeaderTimeRanges] = useState<Map<string, string>>(new Map());

  // Bulk-fetch KPI summaries for all leaders in one request instead of N individual calls.
  const leaderNames = enrichedLeaders.map((l: any) => l.companyName).filter(Boolean);
  const { data: bulkSummaryData } = useAllSummary(leaderNames.length ? leaderNames : null);

  // Fetch the authoritative standalone-companies list once to reconcile companyIds.
  // The bulk-summary endpoint does not return companyId, and the enrichment service
  // can store a stale companyId. This list is the ground truth.
  const { data: companiesListData } = useQuery<any[]>({
    queryKey: ['standalone-companies'],
    queryFn: () => standaloneCompaniesAPI.listCompanies(),
    staleTime: 5 * 60 * 1000,
  });

  // Reconcile `existsInCompanyResearch` and `companyId` for all leaders against
  // live DB state. Both the bulk-summary response and the standalone companies list
  // are fetched fresh on every page load so deletions are reflected immediately.
  React.useEffect(() => {
    const summaries = bulkSummaryData?.summaries;
    const companiesList: any[] = Array.isArray(companiesListData) ? companiesListData : [];

    if (!summaries && companiesList.length === 0) return;

    const normalize = (n: string) => n.toLowerCase().replace(/[^a-z0-9]/g, '');

    // Build a lookup of name → companyId from the authoritative companies list.
    const idByName = new Map<string, string>();
    for (const c of companiesList) {
      if (c.companyName && c.companyId) {
        idByName.set(normalize(c.companyName), c.companyId);
      }
    }

    // Build a set of names confirmed to exist in the DB right now.
    const confirmedInDB = new Set<string>();
    for (const c of companiesList) {
      if (c.companyName) confirmedInDB.add(normalize(c.companyName));
    }

    // Also index by companyId so a name mismatch (e.g. leader "BP" vs Neo4j
    // node "BP P.L.C.") doesn't falsely clear existsInCompanyResearch when
    // the companyId is already known and confirmed in the list.
    const confirmedById = new Set<string>(
      companiesList.map(c => c.companyId).filter(Boolean) as string[]
    );

    setEnrichedLeaders(prev =>
      prev.map(l => {
        let updated = { ...l };
        const s = summaries?.[l.companyName];
        const normalizedName = normalize(l.companyName ?? '');
        const confirmedByIdMatch = Boolean(updated.companyId && confirmedById.has(updated.companyId));

        if (s && s.success && !s.error && s.latest_year != null) {
          // Bulk summary confirmed this company has financial data (`latest_year`
          // is null when the Neo4j node exists but has 0 report records — e.g.
          // UK PARTIAL companies whose filings are PDF-only). Only mark as in
          // research when there is at least one processed financial year.
          updated.existsInCompanyResearch = true;
          // Primary source: companyId returned by bulk-summary (authoritative Neo4j lookup).
          if (s.company_id) {
            updated.companyId = s.company_id;
          }
        } else if (companiesList.length > 0 && !confirmedInDB.has(normalizedName) && !confirmedByIdMatch) {
          // Companies list loaded and this company is not in it (by name OR id)
          // — it was deleted or never properly researched. Clear the stale flag.
          updated.existsInCompanyResearch = false;
          updated.companyId = undefined;
        }

        // Fallback: companies list covers leaders whose bulk-summary didn't
        // resolve a companyId (e.g. newly-added and still processing).
        if (!updated.companyId) {
          const listId = idByName.get(normalizedName);
          if (listId) {
            updated.companyId = listId;
          }
        }

        return updated;
      })
    );
  }, [bulkSummaryData, companiesListData]);

  const liveCountries = useLiveCountriesStore((s) => s.liveCountries);
  const availableCountries = COMPETITOR_COUNTRIES.filter((c) => {
    if (c.value === 'uk') return liveCountries.uk;
    if (c.value === 'india') return liveCountries.india;
    return true; // US always shown
  });

  // Add competitor state
  const [showAddCompetitor, setShowAddCompetitor] = useState(false);
  const [competitorCountry, setCompetitorCountry] = useState<string>(COMPETITOR_COUNTRIES[0].value);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<any>(null);

  const currentCountry = COMPETITOR_COUNTRIES.find((c) => c.value === competitorCountry)
    ?? COMPETITOR_COUNTRIES[0];

  // Stable key over the set of (processing, companyId) leaders. Re-runs of the
  // polling effect should be driven by changes to this set — NOT by every
  // reference change to `enrichedLeaders` (the reconcile / bulk-summary effects
  // produce new array references on most renders, which previously caused the
  // immediate `getCompany(...)` check inside the effect to fire repeatedly).
  const processingCompanyIdsKey = React.useMemo(
    () =>
      enrichedLeaders
        .filter(l => l.isProcessing && l.companyId)
        .map(l => l.companyId as string)
        .sort()
        .join('|'),
    [enrichedLeaders]
  );

  // Poll companies that were just added with a background research task.
  // Once their reportCount > 0 (or the company endpoint reports data is
  // available), clear the `isProcessing` flag so the View link unlocks.
  React.useEffect(() => {
    const companyIds = processingCompanyIdsKey ? processingCompanyIdsKey.split('|') : [];
    if (companyIds.length === 0) return;

    let cancelled = false;
    const PENDING_STATUSES = new Set(['queued', 'in_progress']);
    const checkOne = async (companyId: string) => {
      try {
        const company = await standaloneCompaniesAPI.getCompany(companyId);
        // Mirror Company Research's "Processing..." gating: stay locked while
        // the latest research job is queued/in_progress, regardless of how
        // many reports have already landed. Otherwise Industry Research
        // shows "View in Company Research" before Company Research stops
        // saying "Data is being loaded — please wait", which is the exact
        // inconsistency users hit when the orchestrator's background work
        // is still finishing after the synchronous XBRL fetch returns.
        const status = (company as any)?.latestJobStatus as string | undefined;
        const stillPending = status ? PENDING_STATUSES.has(status) : false;
        // If the backend doesn't surface a job status at all (older data
        // path), fall back to the report-count heuristic so we don't lock
        // forever.
        const ready = status
          ? !stillPending
          : (company?.reportCount ?? 0) > 0 ||
            (company?.processedReportCount ?? 0) > 0 ||
            Boolean(company?.latestPeriod);
        if (ready && !cancelled) {
          setEnrichedLeaders(prev =>
            prev.map(l =>
              l.companyId === companyId ? { ...l, isProcessing: false } : l
            )
          );
        }
      } catch {
        // ignore — we'll retry on the next tick
      }
    };

    const interval = setInterval(() => {
      companyIds.forEach(id => checkOne(id));
    }, 20_000);

    // Delay the first check so the backend has time to write the ResearchJob
    // node (latestJobStatus = 'queued') before we query it. An immediate check
    // can race the job registration and fall back to reportCount, which may
    // already be > 0 from a previous run — incorrectly clearing isProcessing.
    const initialTimer = setTimeout(() => {
      if (!cancelled) companyIds.forEach(id => checkOne(id));
    }, 5_000);

    return () => {
      cancelled = true;
      clearInterval(interval);
      clearTimeout(initialTimer);
    };
  }, [processingCompanyIdsKey]);

  // Mutation for enriching competitive landscape
  const enrichMutation = useMutation({
    mutationFn: () => enrichCompetitiveLandscape(industry.industryId),
    onSuccess: (data) => {
      setEnrichedLeaders(data.marketLeaders);
      toast.success(
        `Enriched ${data.enrichedCount} companies. ${data.companiesInResearch} already in Company Research.`
      );
      queryClient.invalidateQueries({ queryKey: ['industry', industry.industryId] });
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to enrich competitive landscape');
    },
  });

  // Resolve which country pipeline a competitor's "Add to Company Research"
  // should fire. AI-generated market leaders often have an empty `leader.country`,
  // so we also fall back to the industry's `primaryRegions` (or legacy
  // `geographicScope`) — e.g., FMCG / India should route Godrej to the India
  // pipeline even when the leader row itself has no country tag.
  //
  // Returns 'IN' | 'GB' | 'US'. Falls back to 'US' only when neither the leader
  // nor the industry yields a recognised country signal.
  const resolveCountryForPipeline = (leaderCountry: string | undefined): 'IN' | 'GB' | 'US' => {
    const fromLeader = (leaderCountry || '').trim().toUpperCase();
    if (fromLeader === 'IN' || fromLeader === 'INDIA') return 'IN';
    if (fromLeader === 'GB' || fromLeader === 'UK') return 'GB';
    if (fromLeader === 'US' || fromLeader === 'USA' || fromLeader === 'UNITED STATES') return 'US';

    // Fallback to industry geographic scope. Frontend `IndustryDetail` type
    // doesn't declare these fields, but the backend response carries them.
    const industryAny = industry as unknown as {
      primaryRegions?: string[] | null;
      geographicScope?: string | null;
    };
    const scopeCandidate =
      (industryAny.primaryRegions && industryAny.primaryRegions[0]) ||
      industryAny.geographicScope ||
      '';
    const fromIndustry = scopeCandidate.trim().toLowerCase();
    if (fromIndustry.includes('india')) return 'IN';
    if (
      fromIndustry.includes('united kingdom') ||
      fromIndustry === 'uk' ||
      fromIndustry === 'gb' ||
      fromIndustry.includes('britain')
    ) return 'GB';
    return 'US';
  };

  // Extract a companyId hint from a free-form pipeline message
  // (e.g., "ingested company_id=ind_xxx years=5").
  const extractCompanyIdFromMessage = (message?: string | null): string | null => {
    if (!message) return null;
    const m = message.match(/company_id=([a-z0-9_-]+)/i) || message.match(/companyId=([a-z0-9_-]+)/i);
    return m?.[1] ?? null;
  };

  type AddCompanyResult = {
    country: 'IN' | 'GB' | 'US';
    companyId: string | null;
    message?: string;
    alreadyExists: boolean;
    isPartial?: boolean; // UK: true when pipeline found no machine-readable data
  };

  // Mutation for adding a competitive-landscape leader to Company Research.
  //
  // Routing:
  //   • India  → POST /finance/india-financial-data-mcapi     (single canonical node, single call)
  //   • UK     → POST /finance/uk-financial-data              (single canonical node, single call)
  //   • US     → POST /companies/add-from-competitive-landscape  (CIK-based dedup)
  //              + fire-and-forget POST /finance/us-financial-data
  //
  // Why India/UK skip /companies/add-from-competitive-landscape:
  //   the backend's India branch creates an UNCONDITIONAL stub Company node
  //   with the LLM-supplied name ("Marico Limited"), then runs the
  //   Moneycontrol/Screener.in scrape which creates the CANONICAL node. The two
  //   names don't always match, leaving a duplicate empty stub in Neo4j.
  //   Skipping the stub-creating call and going straight to the direct
  //   financial-data REST endpoint gives us exactly one canonical node — its
  //   `data.metadata.company_id` is what we surface back to the UI. The UK
  //   Companies House path has the same risk and we route it the same way.
  // Track which leaders are currently being added so the spinner shows only
  // on the clicked button (not on every "Add to Company Research" button on
  // the page, which is what a shared `mutation.isPending` flag would do).
  const [pendingAddCompanyNames, setPendingAddCompanyNames] = useState<Set<string>>(new Set());

  const addCompanyMutation = useMutation<
    AddCompanyResult,
    Error,
    { companyName: string; cik?: string | null; ticker?: string; country?: string; timeRange?: string }
  >({
    onMutate: (variables) => {
      setPendingAddCompanyNames(prev => {
        const next = new Set(prev);
        next.add(variables.companyName);
        return next;
      });
      // Optimistically show the amber "Fetching financial data…" badge for
      // India and UK both run synchronously (full pipeline awaited in mutationFn),
      // so without this the button just spins with no badge on the leader row.
      // US gets isProcessing from onSuccess (background detection happens there).
      const resolved = resolveCountryForPipeline(variables.country);
      if (resolved === 'IN' || resolved === 'GB') {
        setEnrichedLeaders(prev =>
          prev.map(l =>
            l.companyName === variables.companyName
              ? { ...l, existsInCompanyResearch: true, isProcessing: true }
              : l
          )
        );
      }
    },
    onSettled: (_data, _err, variables) => {
      setPendingAddCompanyNames(prev => {
        if (!prev.has(variables.companyName)) return prev;
        const next = new Set(prev);
        next.delete(variables.companyName);
        return next;
      });
    },
    mutationFn: async ({ companyName, cik, ticker, country, timeRange }) => {
      const resolved = resolveCountryForPipeline(country);

      if (resolved === 'IN') {
        const res = await indiaCompanyAPI.triggerResearch({
          company_name: companyName,
          data_type: 'consolidated',
        });
        const status = (res?.status || '').toLowerCase();
        const errored = ['failed', 'error', 'not_found', 'failure'].includes(status) || Boolean((res as any)?.error);
        if (errored) {
          throw new Error((res as any)?.error || res?.message || `India pipeline failed for ${companyName}`);
        }
        // The envelope is { status, message, data: { metadata: { company_id, … }, … } }.
        // Look at the metadata level first (real shape per IndiaFinancialDataAPIResponse),
        // then fall through to a couple of looser locations in case the route adds them later.
        const payload: any = (res as any)?.data ?? {};
        const companyId =
          payload?.metadata?.company_id ||
          payload?.metadata?.companyId ||
          payload?.company_id ||
          payload?.companyId ||
          extractCompanyIdFromMessage(res?.message);
        return { country: 'IN', companyId: companyId ?? null, message: res?.message, alreadyExists: false };
      }

      if (resolved === 'GB') {
        const res = await ukCompanyAPI.triggerResearch({
          company_name: companyName,
          data_type: 'annual',
          time_range: '5years',
        });
        const status = (res?.status || '').toLowerCase();
        if (status === 'error') {
          throw new Error(res?.message || `UK pipeline failed for ${companyName}`);
        }
        const companyId = res?.data?.metadata?.company_id ?? null;
        // Propagate partial flag so onSuccess knows not to mark the leader as
        // "In Company Research" — the company node was created but no financial
        // data could be extracted (BP files PDF-only, for example).
        const isPartial = status === 'partial';
        return { country: 'GB', companyId, message: res?.message, alreadyExists: false, isPartial };
      }

      // US: the backend's add-from-competitive-landscape already dispatches
      // the SEC pipeline via dispatch_financial_research("US", ...) internally.
      // Do NOT call fetchUSFinancialData here — that would fire a second
      // concurrent SEC run and create duplicate FILED_REPORT nodes in Neo4j.
      const effectiveTimeRange = timeRange || '5years';
      const result = await addCompanyToResearch(companyName, cik, ticker, 'user_001', country, industry.industryId, effectiveTimeRange);
      return {
        country: 'US',
        companyId: result.companyId,
        message: result.message,
        alreadyExists: result.alreadyExists,
      };
    },
    onSuccess: (data, variables) => {
      if (data.country === 'IN') {
        toast.success(`India financial data loaded for ${variables.companyName}.`);
      } else if (data.country === 'GB') {
        if (data.isPartial) {
          toast.warning(
            data.message ?? `No machine-readable financial data available for ${variables.companyName} on Companies House.`,
            { duration: 10000 }
          );
        } else {
          toast.success(`UK financial data loaded for ${variables.companyName}.`);
        }
      } else {
        // US — preserve the original message-based toast logic for parity with
        // the existing UX (handles background-fetch vs alreadyExists vs new).
        const isBackground = data.message?.toLowerCase().includes('background');
        if (isBackground) {
          toast.info(data.message ?? 'Added to Company Research', {
            description: 'Financial data will appear on the company page once processing completes.',
            duration: 8000,
          });
        } else if (data.alreadyExists) {
          toast.info(`${variables.companyName} is already in Company Research`);
        } else {
          toast.success(`${variables.companyName} added to Company Research`);
        }
      }

      // Update local state. Only US uses the 20-second polling effect to
      // unlock the "View" link — India and UK return synchronously, so their
      // data is ready immediately and `isProcessing` is false.
      const isUsBackground =
        data.country === 'US' && Boolean(data.message?.toLowerCase().includes('background'));
      // UK PARTIAL: the company node was created but no financial data was found
      // (e.g. BP files PDF-only on Companies House). Don't mark as "In Company
      // Research" — keep the "Add to Company Research" button visible so the
      // user isn't misled into thinking data is available.
      const hasData = !data.isPartial;
      setEnrichedLeaders(prev =>
        prev.map(leader =>
          leader.companyName === variables.companyName
            ? {
                ...leader,
                existsInCompanyResearch: hasData,
                companyId: hasData ? (data.companyId ?? leader.companyId) : leader.companyId,
                isProcessing: isUsBackground,
              }
            : leader
        )
      );
      queryClient.invalidateQueries({ queryKey: ['industry', industry.industryId] });
      // Belt-and-braces for the "View in Company Research" button: it's gated
      // on `leader.companyId`. We now read that id straight from the India/UK
      // response (`data.metadata.company_id`), but if the route ever returns
      // an envelope without metadata — or for US where the View button relies
      // on the standalone-companies list — invalidating both queries lets the
      // reconciliation effect at the top of this component resolve the
      // canonical companyId by name without requiring a page refresh.
      queryClient.invalidateQueries({ queryKey: ['standalone-companies'] });
      queryClient.invalidateQueries({ queryKey: ['financial', 'bulk-summary'] });
    },
    onError: (error, variables) => {
      toast.error(`Failed to add ${variables.companyName}: ${error.message}`);
      // Revert the optimistic India/UK update so the "Add to Company Research"
      // button reappears on failure.
      const resolved = resolveCountryForPipeline(variables.country);
      if (resolved === 'IN' || resolved === 'GB') {
        setEnrichedLeaders(prev =>
          prev.map(l =>
            l.companyName === variables.companyName
              ? { ...l, existsInCompanyResearch: false, isProcessing: false }
              : l
          )
        );
      }
    },
  });

  // Mutation for adding competitor to landscape
  const addCompetitorMutation = useMutation({
    mutationFn: ({
      companyName,
      cik,
      ticker,
      country,
    }: {
      companyName: string;
      cik?: string;
      ticker?: string;
      country?: string;
    }) => addCompetitorToLandscape(industry.industryId, companyName, cik, ticker, country),
    onSuccess: (data) => {
      if (data.alreadyExists) {
        toast.info(`${data.company.companyName} already exists in competitive landscape`);
      } else {
        toast.success(`${data.company.companyName} added to competitive landscape`);
        // Add the new competitor to local state
        setEnrichedLeaders(prev => {
          const updated = [...prev, data.company];
          // Sort by market share
          updated.sort((a, b) => parseNumber(b.marketShare) - parseNumber(a.marketShare));
          return updated;
        });
      }
      // Reset add competitor UI
      setShowAddCompetitor(false);
      setSearchQuery('');
      setSearchResults([]);
      setSelectedCompany(null);
      queryClient.invalidateQueries({ queryKey: ['industry', industry.industryId] });
    },
    onError: (error: any) => {
      toast.error(`Failed to add competitor: ${error.message || 'Unknown error'}`);
    },
  });

  // Company search with debouncing. Runs TWO sources in parallel:
  //   1. Neo4j (companies already ingested into the platform)
  //   2. External provider for this country (SEC EDGAR / Moneycontrol)
  // Results are merged, deduplicated by name, and Neo4j-backed entries are
  // flagged so the UI can show an "In database" badge. When no exact match
  // is found, a synthetic "+ Add as new company" option is appended so the
  // user can enter a company the backend hasn't seen before.
  React.useEffect(() => {
    if (searchQuery.trim().length < 4) {
      setSearchResults([]);
      return;
    }

    const debounceTimer = setTimeout(async () => {
      setIsSearching(true);
      const query = searchQuery.trim();
      const country = currentCountry;
      try {
        // 1. Database (Neo4j) — always queried regardless of country
        const dbPromise = searchCompaniesInDatabase(query, 10)
          .then((res) => (res.results || [])
            .filter((c) => {
              // Only show DB companies whose country matches the current tab
              if (country.isoCode === 'IN') return c.country === 'INDIA';
              if (country.isoCode === 'US') return c.country === 'US';
              if (country.isoCode === 'GB') return c.country === 'UK';
              return true;
            })
            .map((c) => ({
              companyName: c.company_name,
              ticker: null,
              cik: null,
              exchange: null,
              country: country.isoCode,
              inDatabase: true,
              yearsCount: c.years_count,
              sector: c.sector,
            }))
          )
          .catch((err) => {
            console.warn('DB company search failed:', err);
            return [];
          });

        // 2. External provider
        let externalPromise: Promise<any[]> = Promise.resolve([]);
        if (country.externalProvider === 'moneycontrol') {
          externalPromise = indiaCompanyAPI
            .searchCompanies(query)
            .then((res: any) => (res.results || []).map((c: any) => ({
              companyName: c.stock_name,
              ticker: c.sc_nse_symbol || c.sc_bse_symbol || null,
              cik: null,
              exchange: c.sc_nse_symbol ? 'NSE' : c.sc_bse_symbol ? 'BSE' : null,
              nse: c.sc_nse_symbol,
              bse: c.sc_bse_symbol,
              country: 'IN',
              inDatabase: false,
            })))
            .catch(() => []);
        } else if (country.externalProvider === 'sec') {
          externalPromise = standaloneCompaniesAPI
            .searchSECCompanies(query)
            .then((results: any[]) => results.map((c: any) => ({
              companyName: c.name,
              ticker: c.ticker,
              cik: c.cik,
              exchange: c.exchange,
              country: 'US',
              inDatabase: false,
            })))
            .catch(() => []);
        }

        const [dbResults, externalResults] = await Promise.all([dbPromise, externalPromise]);

        // Merge, deduplicating by lowercased name — DB entries win so the
        // "In database" flag is preserved when both sources return the same
        // company.
        const merged = new Map<string, any>();
        [...dbResults, ...externalResults].forEach((item) => {
          const key = (item.companyName || '').trim().toLowerCase();
          if (!key) return;
          if (!merged.has(key)) merged.set(key, item);
        });
        const combined = Array.from(merged.values());

        // Synthetic "Add as new company" — only when no exact name match exists
        const exact = combined.find(
          (r) => (r.companyName || '').trim().toLowerCase() === query.toLowerCase(),
        );
        if (!exact) {
          combined.push({
            companyName: query,
            ticker: null,
            cik: null,
            exchange: null,
            country: country.isoCode,
            inDatabase: false,
            isCustomEntry: true,
          });
        }

        setSearchResults(combined);
      } catch (error) {
        console.error('Company search failed:', error);
        toast.error('Failed to search companies');
      } finally {
        setIsSearching(false);
      }
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [searchQuery, competitorCountry, currentCountry]);

  const handleSelectCompany = (company: any) => {
    setSelectedCompany(company);
    setSearchResults([]);
    setSearchQuery(company.companyName);
  };

  const handleAddCompetitor = () => {
    if (!selectedCompany) {
      toast.error('Please select a company first');
      return;
    }
    addCompetitorMutation.mutate({
      companyName: selectedCompany.companyName,
      cik: selectedCompany.cik || undefined,
      ticker: selectedCompany.ticker || undefined,
      // Prefer the resolved ISO code from the country config; fall back to
      // whatever the result item carries (DB returns ISO directly).
      country: selectedCompany.country || currentCountry.isoCode,
    });
  };


  const getConfidenceBadgeColor = (confidence: number | null | undefined) => {
    if (!confidence) return 'bg-gray-100 text-gray-800';
    if (confidence >= 0.9) return 'bg-green-100 text-green-800';
    if (confidence >= 0.7) return 'bg-yellow-100 text-yellow-800';
    return 'bg-orange-100 text-orange-800';
  };

  return (
    <div className="space-y-6">
      {/* Action Buttons */}
      <div className="flex justify-end gap-3">
        <button
          onClick={() => setShowAddCompetitor(!showAddCompetitor)}
          className="flex items-center gap-2 px-4 py-2 border border-primary-600 text-primary-600 rounded-lg hover:bg-primary-50"
        >
          <UserPlus className="w-5 h-5" />
          Add Competitor
        </button>
        {competitive && (
          <button
            onClick={() => enrichMutation.mutate()}
            disabled={enrichMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {enrichMutation.isPending ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Sparkles className="w-5 h-5" />
            )}
            {enrichMutation.isPending ? 'Enriching...' : 'Enrich Competitive Landscape'}
          </button>
        )}
      </div>

      {/* Add Competitor Panel */}
      {showAddCompetitor && (
        <div className="bg-white border border-primary-200 rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-neutral-900 flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-primary-600" />
              Add Company to Compare
            </h3>
            <button
              onClick={() => {
                setShowAddCompetitor(false);
                setSearchQuery('');
                setSearchResults([]);
                setSelectedCompany(null);
              }}
              className="p-1 hover:bg-neutral-100 rounded"
            >
              <X className="w-5 h-5 text-neutral-500" />
            </button>
          </div>

          {/* Country selector (dropdown — extensible to more regions) */}
          <div className="mb-4 max-w-sm">
            <Select
              label="Country / Exchange"
              value={competitorCountry}
              onChange={(e) => {
                setCompetitorCountry(e.target.value);
                setSearchQuery('');
                setSearchResults([]);
                setSelectedCompany(null);
              }}
              options={availableCountries.map((c) => ({ value: c.value, label: c.label }))}
            />
          </div>

          {/* Search Input */}
          <div className="relative">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-neutral-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setSelectedCompany(null);
                }}
                placeholder={currentCountry.searchPlaceholder}
                className="w-full pl-10 pr-10 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              {isSearching && (
                <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-primary-600 animate-spin" />
              )}
            </div>

            {/* Search Results Dropdown */}
            {searchResults.length > 0 && !selectedCompany && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg max-h-72 overflow-auto">
                {searchResults.map((company, index) => (
                  <button
                    key={`${company.companyName}-${index}`}
                    onClick={() => handleSelectCompany(company)}
                    className={`w-full px-4 py-3 text-left flex items-center justify-between border-b border-neutral-100 last:border-b-0 ${
                      company.isCustomEntry
                        ? 'bg-primary-50/40 hover:bg-primary-50 border-t border-dashed border-primary-200'
                        : 'hover:bg-primary-50'
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-neutral-900 flex items-center gap-2">
                        {company.isCustomEntry ? (
                          <>
                            <Plus className="w-4 h-4 text-primary-600 flex-shrink-0" />
                            <span>Add as new company: <span className="text-primary-700">"{company.companyName}"</span></span>
                          </>
                        ) : (
                          <span className="truncate">{company.companyName}</span>
                        )}
                      </div>
                      {!company.isCustomEntry && (
                        <div className="text-sm text-neutral-600 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                          {company.cik && <span>CIK: {company.cik}</span>}
                          {company.ticker && <span>Ticker: <span className="font-mono font-semibold text-primary-600">{company.ticker}</span></span>}
                          {company.nse && <span>NSE: <span className="font-mono font-semibold text-primary-600">{company.nse}</span></span>}
                          {company.bse && !company.nse && <span>BSE: <span className="font-mono font-semibold text-primary-600">{company.bse}</span></span>}
                          {company.sector && <span className="text-neutral-500">{company.sector}</span>}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                      {company.inDatabase && (
                        <span className="text-[11px] font-medium bg-emerald-100 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full whitespace-nowrap">
                          ✓ In database
                        </span>
                      )}
                      {company.exchange && !company.inDatabase && (
                        <span className="text-xs bg-neutral-100 px-2 py-1 rounded">{company.exchange}</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Selected Company */}
          {selectedCompany && (
            <div className="mt-4 p-4 bg-primary-50 border border-primary-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold text-neutral-900 flex items-center gap-2">
                    {selectedCompany.companyName}
                    {selectedCompany.inDatabase && (
                      <span className="text-[11px] font-medium bg-emerald-100 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full">
                        ✓ In database
                      </span>
                    )}
                    {selectedCompany.isCustomEntry && (
                      <span className="text-[11px] font-medium bg-amber-100 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full">
                        New company
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-neutral-600 mt-1">
                    {selectedCompany.cik && <>CIK: <span className="font-mono">{selectedCompany.cik}</span></>}
                    {selectedCompany.ticker && (
                      <span className="ml-3">Ticker: <span className="font-mono font-semibold text-primary-600">{selectedCompany.ticker}</span></span>
                    )}
                    {selectedCompany.nse && (
                      <span className="ml-3">NSE: <span className="font-mono font-semibold text-primary-600">{selectedCompany.nse}</span></span>
                    )}
                    {selectedCompany.bse && !selectedCompany.nse && (
                      <span className="ml-3">BSE: <span className="font-mono font-semibold text-primary-600">{selectedCompany.bse}</span></span>
                    )}
                    {selectedCompany.isCustomEntry && (
                      <span className="text-amber-700">
                        This company isn't in our database yet — it will be added and analysed.
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => {
                    setSelectedCompany(null);
                    setSearchQuery('');
                  }}
                  className="p-1 hover:bg-primary-100 rounded"
                >
                  <X className="w-4 h-4 text-neutral-500" />
                </button>
              </div>
            </div>
          )}

          {/* Add Button */}
          <div className="mt-4 flex justify-end">
            <button
              onClick={handleAddCompetitor}
              disabled={!selectedCompany || addCompetitorMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {addCompetitorMutation.isPending ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Adding & Analyzing...
                </>
              ) : (
                <>
                  <Plus className="w-5 h-5" />
                  Add to Competitive Landscape
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Empty state when no landscape exists yet */}
      {!competitive && (
        <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-10 text-center">
          <Users className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
          <p className="text-neutral-600 font-medium mb-1">No competitive landscape yet</p>
          <p className="text-sm text-neutral-400">
            Add competitors above or click "Refresh Research" to auto-generate.
          </p>
        </div>
      )}

      {/* Competitive Metrics + Leaders — only when landscape exists */}
      {competitive && (<>
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white border border-neutral-200 rounded-lg p-6 flex flex-col justify-between min-h-[100px]">
          <div className="text-sm font-medium text-neutral-500">Intensity</div>
          <div className="text-xl font-bold text-neutral-900 capitalize mt-3">
            {(competitive.competitiveIntensity || 'N/A').replace('_', ' ')}
          </div>
        </div>
        <div className="bg-white border border-neutral-200 rounded-lg p-6 flex flex-col justify-between min-h-[100px]">
          <div className="text-sm font-medium text-neutral-500">Concentration</div>
          <div className="text-xl font-bold text-neutral-900 capitalize mt-3">
            {(competitive.marketConcentration || 'N/A').replace('_', ' ')}
          </div>
        </div>
        <div className="bg-white border border-neutral-200 rounded-lg p-6 flex flex-col justify-between min-h-[100px]">
          <div className="text-sm font-medium text-neutral-500">Top Competitors</div>
          <div className="text-xl font-bold text-neutral-900 mt-3">
            {competitive.topCompetitorCount || enrichedLeaders.length || 'N/A'}
          </div>
        </div>
        <div className="bg-white border border-neutral-200 rounded-lg p-6 flex flex-col justify-between min-h-[100px]">
          <div className="text-sm font-medium text-neutral-500">Barrier to Entry</div>
          <div className="text-xl font-bold text-neutral-900 capitalize mt-3">
            {(competitive.barrierToEntry || 'N/A').replace('_', ' ')}
          </div>
        </div>
      </div>

      {/* Market Leaders */}
      {enrichedLeaders && enrichedLeaders.length > 0 && (
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-neutral-900 mb-4">
            Market Leaders
          </h2>
          <div className="space-y-4">
            {enrichedLeaders.map((leader, index) => (
              <div
                key={index}
                className="border border-neutral-200 rounded-lg p-4"
              >
                {/* Header with company name and market share */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-neutral-900">
                        {leader.companyName}
                      </h3>

                      {/* Status badges */}
                      {leader.existsInCompanyResearch && !leader.isProcessing && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                          <Building2 className="w-3 h-3" />
                          In Company Research
                        </span>
                      )}
                      {leader.existsInCompanyResearch && leader.isProcessing && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-medium">
                          <Loader2 className="w-3 h-3 animate-spin" />
                          Fetching financial data…
                        </span>
                      )}

                      {leader.secMatchConfidence !== null && leader.secMatchConfidence !== undefined && (
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getConfidenceBadgeColor(leader.secMatchConfidence)}`}>
                          {(leader.secMatchConfidence * 100).toFixed(0)}% match
                        </span>
                      )}
                    </div>

                    {/* CIK and Ticker */}
                    <div className="flex items-center gap-4 text-sm text-neutral-600">
                      {leader.cik && (
                        <div className="flex items-center gap-1">
                          <span className="font-medium">CIK:</span>
                          <span className="font-mono">{leader.cik}</span>
                        </div>
                      )}
                      {leader.ticker && (
                        <div className="flex items-center gap-1">
                          <span className="font-medium">Ticker:</span>
                          <span className="font-mono font-semibold text-primary-600">
                            {leader.ticker}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Action buttons */}
                    <div className="mt-2 flex gap-2 flex-wrap items-center">
                      {(leader.cik || leader.country === 'GB' || leader.country === 'UK' || !leader.existsInCompanyResearch) && !leader.existsInCompanyResearch && (() => {
                        const isThisLeaderAdding = pendingAddCompanyNames.has(leader.companyName);
                        const isUS = leader.cik && leader.country !== 'GB' && leader.country !== 'UK';
                        const selectedRange = leaderTimeRanges.get(leader.companyName) ?? '5years';
                        return (
                          <div className="flex items-center gap-2">
                            {isUS && (
                              <select
                                value={selectedRange}
                                onChange={(e) => setLeaderTimeRanges(prev => new Map(prev).set(leader.companyName, e.target.value))}
                                disabled={isThisLeaderAdding}
                                className="text-sm border border-neutral-300 rounded-md px-2 py-1 bg-white text-neutral-700 disabled:opacity-50"
                              >
                                <option value="1year">1 year</option>
                                <option value="2years">2 years</option>
                                <option value="3years">3 years</option>
                                <option value="5years">5 years</option>
                                <option value="10years">10 years</option>
                              </select>
                            )}
                            <button
                              onClick={() => addCompanyMutation.mutate({
                                companyName: leader.companyName,
                                cik: leader.cik || undefined,
                                ticker: leader.ticker || undefined,
                                country: leader.country || undefined,
                                timeRange: isUS ? selectedRange : undefined,
                              })}
                              disabled={isThisLeaderAdding}
                              className="inline-flex items-center gap-1 px-3 py-1 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                            >
                              {isThisLeaderAdding ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Plus className="w-4 h-4" />
                              )}
                              Add to Company Research
                            </button>
                          </div>
                        );
                      })()}

                      {leader.existsInCompanyResearch && leader.companyId && !leader.isProcessing && (
                        <a
                          href={`/companies/${leader.companyId}`}
                          className="inline-flex items-center gap-1 px-3 py-1 border border-primary-600 text-primary-600 rounded-md hover:bg-primary-50 text-sm"
                        >
                          <ExternalLink className="w-4 h-4" />
                          View in Company Research
                        </a>
                      )}
                      {leader.existsInCompanyResearch && leader.companyId && leader.isProcessing && (
                        <button
                          type="button"
                          disabled
                          title="Financial data is still being fetched. This will be enabled once processing completes."
                          className="inline-flex items-center gap-1 px-3 py-1 border border-neutral-300 text-neutral-400 rounded-md text-sm cursor-not-allowed"
                        >
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Processing…
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="text-right ml-4">
                    <div className="text-2xl font-bold text-primary-600">
                      {parseNumber(leader.marketShare).toFixed(1)}%
                    </div>
                    {leader.revenue && (
                      <div className="text-sm text-neutral-600">
                        {formatLargeCurrency(parseNumber(leader.revenue))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Live KPI badges — populated from financial data when available */}
                <CompanyKPIBadges
                  companyName={leader.companyName}
                  summary={bulkSummaryData?.summaries?.[leader.companyName]}
                />

                {/* Strengths and Weaknesses */}
                <div className="grid grid-cols-2 gap-4 mt-3 pt-3 border-t border-neutral-200">
                  {leader.strengths && leader.strengths.length > 0 && (
                    <div>
                      <div className="text-xs font-medium text-neutral-600 mb-2">
                        Strengths
                      </div>
                      <ul className="space-y-1">
                        {leader.strengths.map((strength, i) => (
                          <li
                            key={i}
                            className="text-sm text-green-700 flex items-start gap-2"
                          >
                            <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            {strength}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {leader.weaknesses && leader.weaknesses.length > 0 && (
                    <div>
                      <div className="text-xs font-medium text-neutral-600 mb-2">
                        Weaknesses
                      </div>
                      <ul className="space-y-1">
                        {leader.weaknesses.map((weakness, i) => (
                          <li
                            key={i}
                            className="text-sm text-red-700 flex items-start gap-2"
                          >
                            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            {weakness}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Emerging Players */}
      {competitive.emergingPlayers && competitive.emergingPlayers.length > 0 && (
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-neutral-900 mb-4">
            Emerging Players
          </h2>
          <div className="flex flex-wrap gap-2">
            {competitive.emergingPlayers.map((player, index) => (
              <span
                key={index}
                className="px-3 py-1.5 bg-blue-100 text-blue-800 rounded-full text-sm font-medium"
              >
                {player}
              </span>
            ))}
          </div>
        </div>
      )}

      </>)}
    </div>
  );
};

const TechnologyTab: React.FC<{ industry: IndustryDetailType }> = ({
  industry,
}) => {
  const technology = industry.technologyTrends;

  if (!technology) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
        <Info className="w-12 h-12 text-blue-500 mx-auto mb-3" />
        <p className="text-blue-700">Technology data available after research generation</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tech Metrics */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white border border-neutral-200 rounded-lg p-6 flex flex-col justify-between min-h-[100px]">
          <div className="text-sm font-medium text-neutral-500">Adoption Stage</div>
          <div className="text-xl font-bold text-neutral-900 capitalize mt-3">
            {technology.adoptionStage}
          </div>
        </div>
        <div className="bg-white border border-neutral-200 rounded-lg p-6 flex flex-col justify-between min-h-[100px]">
          <div className="text-sm font-medium text-neutral-500">Digital Maturity</div>
          <div className="text-xl font-bold text-neutral-900 capitalize mt-3">
            {technology.digitalMaturity}
          </div>
        </div>
        <div className="bg-white border border-neutral-200 rounded-lg p-6 flex flex-col justify-between min-h-[100px]">
          <div className="text-sm font-medium text-neutral-500">R&D Spend Trend</div>
          <div className="text-xl font-bold text-neutral-900 capitalize mt-3">
            {technology.rdSpendTrend}
          </div>
        </div>
        <div className="bg-white border border-neutral-200 rounded-lg p-6 flex flex-col justify-between min-h-[100px]">
          <div className="text-sm font-medium text-neutral-500">Tech Readiness</div>
          <div className="text-xl font-bold text-neutral-900 capitalize mt-3">
            {technology.technologyReadiness}
          </div>
        </div>
      </div>

      {/* Key Technologies */}
      {technology.keyTechnologies && technology.keyTechnologies.length > 0 && (
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-neutral-900 mb-4">
            Key Technologies
          </h2>
          <div className="space-y-4">
            {technology.keyTechnologies.map((tech, index) => (
              <div
                key={index}
                className="border border-neutral-200 rounded-lg p-4"
              >
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-lg font-semibold text-neutral-900">
                    {tech.name}
                  </h3>
                  {tech.adoptionRate && (
                    <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                      {tech.adoptionRate}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  {tech.maturityLevel && (
                    <div>
                      <span className="text-neutral-600">Maturity: </span>
                      <span className="font-medium text-neutral-900 capitalize">
                        {tech.maturityLevel}
                      </span>
                    </div>
                  )}
                  {tech.impact && (
                    <div>
                      <span className="text-neutral-600">Impact: </span>
                      <span className="font-medium text-neutral-900 capitalize">
                        {tech.impact}
                      </span>
                    </div>
                  )}
                  {tech.timeline && (
                    <div>
                      <span className="text-neutral-600">Timeline: </span>
                      <span className="font-medium text-neutral-900">
                        {tech.timeline}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Innovation Drivers */}
      {technology.innovationDrivers && technology.innovationDrivers.length > 0 && (
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-neutral-900 mb-4">
            Innovation Drivers
          </h2>
          <ul className="space-y-2">
            {technology.innovationDrivers.map((driver, index) => (
              <li key={index} className="flex items-start gap-3">
                <Zap className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                <span className="text-neutral-700">{driver}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const DisruptionsTab: React.FC<{ industry: IndustryDetailType }> = ({
  industry,
}) => {
  const disruptions = industry.disruptions;

  if (!disruptions) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
        <Info className="w-12 h-12 text-blue-500 mx-auto mb-3" />
        <p className="text-blue-700">Disruptions data available after research generation</p>
      </div>
    );
  }

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-100 text-red-800';
      case 'high':
        return 'bg-orange-100 text-orange-800';
      case 'moderate':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  return (
    <div className="space-y-6">
      {/* Disruption Risk Meter */}
      {disruptions.disruptionRisk && (
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-neutral-900 mb-4">
            Overall Disruption Risk
          </h2>
          <div
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-lg font-bold ${
              disruptions.disruptionRisk === 'critical'
                ? 'bg-red-100 text-red-800'
                : disruptions.disruptionRisk === 'high'
                ? 'bg-orange-100 text-orange-800'
                : disruptions.disruptionRisk === 'moderate'
                ? 'bg-yellow-100 text-yellow-800'
                : 'bg-green-100 text-green-800'
            }`}
          >
            <AlertTriangle className="w-6 h-6" />
            {disruptions.disruptionRisk.toUpperCase()}
          </div>
        </div>
      )}

      {/* Disruptive Forces */}
      {disruptions.disruptiveForces && disruptions.disruptiveForces.length > 0 && (
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-neutral-900 mb-4">
            Disruptive Forces
          </h2>
          <div className="space-y-4">
            {disruptions.disruptiveForces.map((force, index) => (
              <div
                key={index}
                className="border border-neutral-200 rounded-lg p-4"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-neutral-900 mb-1">
                      {force.name}
                    </h3>
                    <div className="flex items-center gap-2 text-sm text-neutral-600">
                      <span className="capitalize">{force.type}</span>
                      <span>•</span>
                      <span className="capitalize">{force.timeline}</span>
                    </div>
                  </div>
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-medium ${getSeverityColor(
                      force.severity
                    )}`}
                  >
                    {force.severity}
                  </span>
                </div>
                <p className="text-sm text-neutral-700 mb-2">{force.impact}</p>
                <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm">
                  <span className="font-medium text-blue-900">Mitigation: </span>
                  <span className="text-blue-700">{force.mitigation}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Regulatory & Other Risks */}
      <div className="grid grid-cols-2 gap-6">
        {disruptions.regulatoryChanges &&
          disruptions.regulatoryChanges.length > 0 && (
            <div className="bg-white border border-neutral-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-neutral-900 mb-3">
                Regulatory Changes
              </h3>
              <ul className="space-y-2">
                {disruptions.regulatoryChanges.map((change, index) => (
                  <li
                    key={index}
                    className="text-sm text-neutral-700 flex items-start gap-2"
                  >
                    <Shield className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                    {change}
                  </li>
                ))}
              </ul>
            </div>
          )}

        {disruptions.geopoliticalRisks &&
          disruptions.geopoliticalRisks.length > 0 && (
            <div className="bg-white border border-neutral-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-neutral-900 mb-3">
                Geopolitical Risks
              </h3>
              <ul className="space-y-2">
                {disruptions.geopoliticalRisks.map((risk, index) => (
                  <li
                    key={index}
                    className="text-sm text-neutral-700 flex items-start gap-2"
                  >
                    <Globe className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                    {risk}
                  </li>
                ))}
              </ul>
            </div>
          )}
      </div>
    </div>
  );
};

const VersionsTab: React.FC<{
  industry: IndustryDetailType;
  versions?: IndustryVersion[];
  timeline?: IndustryTimeline;
}> = ({ industry, versions, timeline }) => {
  if (!timeline) {
    return (
      <div className="bg-white border border-neutral-200 rounded-lg p-6 text-center">
        <Calendar className="w-12 h-12 text-neutral-400 mx-auto mb-3" />
        <p className="text-neutral-600">Loading version history...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Timeline Summary */}
      <div className="bg-white border border-neutral-200 rounded-lg p-6">
        <h2 className="text-xl font-semibold text-neutral-900 mb-4">
          Version Timeline
        </h2>
        <p className="text-neutral-600 mb-4">
          Total versions: <span className="font-semibold">{timeline.totalVersions}</span>
        </p>

        {/* Timeline */}
        <div className="space-y-4">
          {timeline.timeline.map((version, index) => (
            <div
              key={version.versionId}
              className="border-l-4 border-primary-600 pl-4 pb-4"
            >
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="text-lg font-semibold text-neutral-900">
                    {version.semanticVersion}
                  </h3>
                  <p className="text-sm text-neutral-600">
                    {version.reportPeriod} •{' '}
                    {new Date(version.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    version.changeType === 'major'
                      ? 'bg-red-100 text-red-800'
                      : version.changeType === 'minor'
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-blue-100 text-blue-800'
                  }`}
                >
                  {version.changeType}
                </span>
              </div>
              {version.changeDescription && (
                <p className="text-sm text-neutral-700 mt-2">
                  {version.changeDescription}
                </p>
              )}
              <div className="mt-2 text-sm text-neutral-600">
                Materiality Score:{' '}
                <span className="font-medium">
                  {(version.materialityScore * 100).toFixed(0)}%
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default IndustryDetail;
